import { BOOK_APPOINTMENT_PATH, SIGNATURE_BOOKING_PATH, VIRTUAL_BOOKING_PATH } from '@/lib/consultationBooking';

export function getRecommendationBookingPath(href) {
  return [SIGNATURE_BOOKING_PATH, VIRTUAL_BOOKING_PATH].includes(href) ? href : BOOK_APPOINTMENT_PATH;
}

import { canMeasure } from '@/lib/privacy';
import { getTrackingToken } from '@/lib/trackEvent';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import trackEvent from '@/lib/trackEvent';

const PersonalizationContext = createContext();

// Compute local interest scores from all stored signals
function computeLocalProfile() {
  const scores = {};
  const add = (key, val) => { scores[key] = (scores[key] || 0) + val; };

  // Browsing history
  try {
    const raw = localStorage.getItem('trinity-browsing-history');
    const history = raw ? JSON.parse(raw) : [];
    history.forEach(({ page = '', timeSpent = 0, scrollDepth = 0 }) => {
      const p = page.toLowerCase();
      const engagement = 1 + (timeSpent > 60 ? 1 : 0) + (scrollDepth > 60 ? 1 : 0);
      if (p.includes('implant') || p.includes('allon4') || p.includes('all-on-4') || p.includes('fullmouth')) { add('dental-implants', 3 * engagement); add('all-on-4', p.includes('allon4') || p.includes('all-on-4') ? 2 * engagement : 0); }
      if (p.includes('cosmetic') || p.includes('veneer') || p.includes('smile')) { add('cosmetic-dentistry', 2 * engagement); }
      if (p.includes('invisalign') || p.includes('aligner')) { add('invisalign', 2 * engagement); }
      if (p.includes('emergency')) { add('emergency-care', 4 * engagement); }
      if (p.includes('financing') || p.includes('payment') || p.includes('cost') || p.includes('price')) { add('price-sensitive', 2 * engagement); }
      if (p.includes('blog') || p.includes('education') || p.includes('faq')) { add('research-mode', 1 * engagement); }
      if (p.includes('about') || p.includes('doctor') || p.includes('results')) { add('high-intent', 2 * engagement); }
    });
  } catch (_) {}

  // Click / interaction history
  try {
    const raw = localStorage.getItem('trinity-interactions');
    const interactions = raw ? JSON.parse(raw) : [];
    interactions.forEach(({ type = '', target = '' }) => {
      const t = target.toLowerCase();
      if (type === 'form-submit') { add('high-intent', 10); add('conversion', 5); }
      if (type === 'cta-click') { add('high-intent', 5); }
      if (type === 'phone-click') { add('high-intent', 4); add('emergency-care', t.includes('emergency') ? 3 : 0); }
      if (type === 'service-view') {
        if (t.includes('implant')) add('dental-implants', 3);
        if (t.includes('cosmetic') || t.includes('veneer')) add('cosmetic-dentistry', 3);
        if (t.includes('invisalign')) add('invisalign', 3);
        if (t.includes('financing')) add('price-sensitive', 3);
      }
    });
  } catch (_) {}

  // Form submissions
  try {
    const raw = null;
    const submissions = raw ? JSON.parse(raw) : [];
    submissions.forEach(({ formType = '', interests = [] }) => {
      add('high-intent', 8);
      if (formType === 'appointment') add('conversion', 10);
      interests.forEach(i => add(i, 3));
    });
  } catch (_) {}

  return scores;
}

function deriveSegment(scores, pageViews) {
  if (!scores || Object.keys(scores).length === 0) return 'preventative_care';

  const top = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [topKey, topVal] = top[0] || [];

  if (scores['emergency-care'] >= 4) return 'emergency_care';
  if (scores['conversion'] >= 5) return 'high_intent_implants'; // submitted form
  if (topKey === 'dental-implants' && topVal >= 6) return 'high_intent_implants';
  if (topKey === 'cosmetic-dentistry' && topVal >= 4) return 'cosmetic_focused';
  if (topKey === 'invisalign' && topVal >= 4) return 'cosmetic_focused';
  if (scores['price-sensitive'] >= 4) return 'price_sensitive';
  if (scores['high-intent'] >= 8 || (pageViews >= 4 && topVal >= 4)) return 'high_intent_implants';
  if (scores['research-mode'] >= 4) return 'comparison_shopper';
  return 'preventative_care';
}

export function AIPersonalizationProvider({ children }) {
  const [sessionId, setSessionId] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!canMeasure()) { setLoading(false); return; }
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('session_id', sid);
    }
    setSessionId(sid);

    // Build local profile immediately for instant personalization
    const localScores = computeLocalProfile();
    const pageViews = parseInt(localStorage.getItem('trinity-page-views') || '0', 10);
    const segment = deriveSegment(localScores, pageViews);
    const topInterests = Object.entries(localScores)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);

    setUserProfile({
      personalization_segment: segment,
      interests: topInterests,
      interest_scores: localScores,
      browsing_history: (() => { try { return JSON.parse(localStorage.getItem('trinity-browsing-history') || '[]'); } catch { return []; } })(),
      interaction_history: (() => { try { return JSON.parse(localStorage.getItem('trinity-interactions') || '[]'); } catch { return []; } })(),
      page_views: pageViews
    });

    // Capture demographics async (non-blocking)
    // Geolocation collection removed.
  }, []);



  const hasFetchFailedRef = useRef(false);

  const fetchRecommendations = useCallback(async (currentPage, forceRefresh = false) => {
    if (!canMeasure() || !sessionId) return;
    if (hasFetchFailedRef.current && !forceRefresh) return;

    const cacheKey = `recommendations_${sessionId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached && !forceRefresh) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        setRecommendations(data.recommendations);
        if (data.user_profile) setUserProfile(prev => ({ ...prev, ...data.user_profile }));
        setLoading(false);
        return;
      }
    }

    try {
      const trafficSource = document.referrer ? (() => { try { return new URL(document.referrer).hostname; } catch { return 'Direct'; } })() : 'Direct';
      const campaignData = (() => { try { return JSON.parse(localStorage.getItem('campaign_data') || 'null'); } catch { return null; } })();

      const response = await Promise.resolve(
        base44.functions.invoke('generatePersonalizedRecommendations', {
          session_id: sessionId,
          write_token: getTrackingToken('interest', sessionId),
          current_page: currentPage,
          traffic_source: trafficSource,
          campaign_data: campaignData
        })
      ).catch(() => null);

      if (!response) throw new Error('fetch failed');

      if (response.data?.recommendations) {
        setRecommendations(response.data.recommendations);
        if (response.data.user_profile) {
          setUserProfile(prev => ({ ...prev, ...response.data.user_profile }));
        }
        localStorage.setItem(cacheKey, JSON.stringify({ data: response.data, timestamp: Date.now() }));
      }
    } catch (err) {
      // Swallow all errors silently — this is non-critical personalization
      // Avoid rethrowing so the error doesn't become an unhandled rejection
      hasFetchFailedRef.current = true;
      if (!recommendations) {
        setRecommendations({
          primary_cta: { text: "Schedule Your Consultation", action: "book-appointment", urgency_level: "medium" },
          personalized_message: "Transform your smile today",
          engagement_strategy: "promotional"
        });
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Record a page visit into persistent browsing history
  const trackPageVisit = useCallback((pageName, category = 'other', timeSpent = 0, scrollDepth = 0) => {
    if (!canMeasure()) return;
    try {
      const raw = localStorage.getItem('trinity-browsing-history');
      const history = raw ? JSON.parse(raw) : [];
      history.unshift({ page: pageName, category, timeSpent, scrollDepth, timestamp: Date.now() });
      localStorage.setItem('trinity-browsing-history', JSON.stringify(history.slice(0, 30)));
      // Increment page view counter
      const pv = parseInt(localStorage.getItem('trinity-page-views') || '0', 10) + 1;
      localStorage.setItem('trinity-page-views', String(pv));
      // Recompute local profile
      const newScores = computeLocalProfile();
      const segment = deriveSegment(newScores, pv);
      setUserProfile(prev => ({
        ...prev,
        browsing_history: history.slice(0, 30),
        interest_scores: newScores,
        interests: Object.entries(newScores).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k]) => k),
        personalization_segment: segment,
        page_views: pv
      }));
    } catch (_) {}
  }, []);

  // Record a user interaction (click, form submit, etc.)
  const trackInteraction = useCallback(async (interactionType, target, metadata = {}) => {
    if (!canMeasure() || /form|patient|photo/i.test(interactionType)) return;
    metadata = {};
    // Persist locally for instant future personalization
    try {
      const raw = localStorage.getItem('trinity-interactions');
      const interactions = raw ? JSON.parse(raw) : [];
      interactions.unshift({ type: interactionType, target, timestamp: Date.now(), metadata });
      localStorage.setItem('trinity-interactions', JSON.stringify(interactions.slice(0, 50)));
    } catch (_) {}

    // Recompute profile after significant actions
    if (['cta-click', 'form-submit', 'service-view', 'phone-click'].includes(interactionType)) {
      const newScores = computeLocalProfile();
      const pv = parseInt(localStorage.getItem('trinity-page-views') || '0', 10);
      const segment = deriveSegment(newScores, pv);
      setUserProfile(prev => ({
        ...prev,
        interest_scores: newScores,
        interests: Object.entries(newScores).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k]) => k),
        personalization_segment: segment
      }));
    }

    // Sync to DB in background (non-blocking) — only for authenticated users
    if (!canMeasure() || !sessionId) return;
    Promise.resolve().then(async () => {
      try {
        const isAuthed = await base44.auth.isAuthenticated();
        if (!isAuthed) return;
        await trackEvent('interest', {
          session_id: sessionId,
          append_interaction: { type: interactionType, target, timestamp: new Date().toISOString(), metadata },
        });
        if (['cta-click', 'form-submit', 'service-view'].includes(interactionType)) {
          fetchRecommendations(window.location.pathname, true);
        }
      } catch (_) {}
    }).catch(() => {});
  }, [sessionId, fetchRecommendations]);

  // Record a form submission for strong intent signals
  const trackFormSubmission = useCallback(() => {}, []);

  const value = {
    sessionId,
    recommendations,
    userProfile,
    loading,
    fetchRecommendations,
    trackInteraction,
    trackPageVisit,
    trackFormSubmission
  };

  return (
    <PersonalizationContext.Provider value={value}>
      {children}
    </PersonalizationContext.Provider>
  );
}

const DEFAULT_CONTEXT = {
  sessionId: null,
  recommendations: null,
  userProfile: null,
  loading: false,
  fetchRecommendations: () => {},
  trackInteraction: () => {},
  trackPageVisit: () => {},
  trackFormSubmission: () => {},
};

export function useAIPersonalization() {
  const context = useContext(PersonalizationContext);
  return context || DEFAULT_CONTEXT;
}